import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { VERSION_CONSTANTS } from './version.constants';
import { WINDOW } from './window.token';

/**
 * Catches Angular's `ChunkLoadError` raised when the bundle in memory tries
 * to load a hashed chunk that no longer exists on the server (typical after
 * a deploy with `outputHashing: 'all'`).
 *
 * Strategy: reload the page once. A sessionStorage marker prevents an
 * infinite reload loop if the new bundle keeps failing for an unrelated
 * reason. The marker is timestamped and expires after a short TTL so a
 * deploy that lands minutes later still benefits from auto-recovery.
 *
 * NavigationEnd clears the marker; once the user has navigated successfully
 * on the new bundle, the next chunk failure is treated as a fresh incident.
 */
@Injectable({ providedIn: 'root' })
export class CesGlobalErrorHandler implements ErrorHandler {
  private readonly router = inject(Router);
  private readonly window = inject(WINDOW);
  private readonly zone = inject(NgZone);
  private markerSubscribed = false;

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && this.shouldReload()) {
      this.markReloadAttempt();
      this.window.location.reload();
      return;
    }
    // Delegate to Angular's default behavior (logs to console in dev,
    // dispatches to ErrorReporter in prod).
    console.error(error);
  }

  private isChunkLoadError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const err = error as { name?: unknown; message?: unknown; rejection?: { name?: unknown; message?: unknown } };
    const rejection = err.rejection;
    const name = String(err.name ?? rejection?.name ?? '');
    const message = String(err.message ?? rejection?.message ?? '');
    return (
      name === 'ChunkLoadError' ||
      /Loading chunk \S+ failed/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message)
    );
  }

  private shouldReload(): boolean {
    const stamp = this.readMarker();
    if (stamp === null) return true;
    return Date.now() - stamp > VERSION_CONSTANTS.CHUNK_RELOAD_MARKER_TTL_MS;
  }

  private readMarker(): number | null {
    try {
      const raw = sessionStorage.getItem(VERSION_CONSTANTS.CHUNK_RELOAD_MARKER_KEY);
      if (raw === null) return null;
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private markReloadAttempt(): void {
    try {
      sessionStorage.setItem(VERSION_CONSTANTS.CHUNK_RELOAD_MARKER_KEY, String(Date.now()));
    } catch {
      // sessionStorage write can fail in private mode; the reload will still
      // happen, the loop protection just degrades to "always reload once".
    }
    this.bindNavigationCleanup();
  }

  private bindNavigationCleanup(): void {
    if (this.markerSubscribed) return;
    this.markerSubscribed = true;
    this.zone.runOutsideAngular(() => {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          try {
            sessionStorage.removeItem(VERSION_CONSTANTS.CHUNK_RELOAD_MARKER_KEY);
          } catch {
            // Swallow; see markReloadAttempt above.
          }
        }
      });
    });
  }
}
